import React, {useCallback, useEffect, useMemo, useState} from 'react';
import LocationProfileSimilarYardsNearby from '../../components/location/profile/LocationProfileSimilarYardsNearby';
import Busy from '../../components/Busy';
import {Grid} from '@mui/material';
import {
  PaymentContainer,
  PaymentHeading,
  PaymentLpDivider,
  PaymentLpListingInfoSection,
  PaymentMainContainer,
  PaymentMainSection,
  PaymentSection
} from './style';
import BookingDetails from '../../components/bookingPayment/details/BookingDetails';
import BookingPaymentInfo from './BookingPaymentInfo';
import RequestSpace from '../../components/bookingPayment/requestSpace/RequestSpace';
import {calculateRate, DAILY, MONTHLY, ONE_TIME, RECURRING} from '../../util/BookingUtil';
import {calculateBookingChargeAmount} from '../requests/booking-requests';
import {calculateNumberOfDays} from '../../util/DateUtils';
import BookingConfirmation from '../../components/bookingPayment/confirmation/BookingConfirmation';
import {requestPaymentMethods} from '../../components/paymentMethods/request/payment-method-requests';
import {DateFormats} from '../../components/constants/securspace-constants';
import moment from "moment";
import {getErrorMessageForNonStandardAndStandardResponse} from '../../util/NetworkErrorUtil';
import useLocationHasRequestedCapacity from "../../hooks/components/location/useLocationHasRequestedCapacity";
import {getEndDateMonthly} from "../../controls/FrequencyOption";
import AddPaymentMethodSetup from "./AddPaymentMethodSetup";

const BookingPaymentBody = React.forwardRef(({
                                               account,
                                               snackbarShowMessage,
                                               profileId,
                                               bookingLocation,
                                               defaultBookingData,
                                               bookingStaticData,
                                               rentalAgreementURL,
                                               assetTypeList,
                                               isBrokeredBooking,
                                               attributionSource
                                             }, ref) => {
  const [startDate, setStartDate] = useState(defaultBookingData?.fromDate ? defaultBookingData.fromDate : moment().format(DateFormats.DAY));
  const [endDate, setEndDate] = useState(defaultBookingData?.toDate ? defaultBookingData.toDate : moment().add(1, 'days').format(DateFormats.DAY));
  const [assetType, setAssetType] = useState(defaultBookingData?.type ? defaultBookingData.type : '');
  const [numberSpaces, setNumberSpaces] = useState(defaultBookingData?.space ? defaultBookingData.space : 0)

  const [costData, setCostData] =
    useState({
      duration: 0,
      initialTotalCharge: 0,
      initialProcessingFee: 0,
      initialCharge: 0,
      recurringTotalCharge: 0,
      recurringProcessingFee: 0,
      recurringCharge: 0,
      firstRecurringPaymentDate: '',
      initialBookingTaxAmount: 0,
      recurringBookingTaxAmount: 0
    });
  const [hasAcceptedRentalAgreement, setHasAcceptedRentalAgreement] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingNumber, setBookingNumber] = useState();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [disableBookingChanges, setDisableBookingChanges] = useState(false);
  const [hasFinishedEditingBooking, setHasFinishedEditingBooking] = useState(true);
  // Indicates if there was an error calculating cost data
  const [costDataError, setCostDataError] = useState(false);
  const [loadingCostData, setLoadingCostData] = useState(false);

  const locationHasRequestedCapacity = useLocationHasRequestedCapacity(
    profileId,
    numberSpaces,
    startDate,
    moment(endDate).format(DateFormats.DAY),
    bookingStaticData.monthly ? MONTHLY : DAILY,
  );

  const filterAndSetPaymentMethods = useCallback((paymentMethods) => {
    if (paymentMethods && paymentMethods.length === 0) {
      snackbarShowMessage('You must add a payment method before booking a space.', 'error', 15000);
      return;
    }

    if (paymentMethods.length > 0) {
      const validPaymentMethods = paymentMethods.filter((paymentMethod) => {
        return paymentMethod?.dwollaStatus !== 'unverified' && paymentMethod?.stripeStatus !== 'unverified';
      });

      if (validPaymentMethods && validPaymentMethods.length === 0) {
        snackbarShowMessage('You must add a verified payment method before booking a space.', 'error', 15000);
        return;
      }

      setPaymentMethods(validPaymentMethods);
      setSelectedPaymentMethodId(validPaymentMethods[0].id);
    }
  }, [snackbarShowMessage]);

  const loadPaymentMethods = useCallback(() => {
    if (!isBrokeredBooking) {
      Busy.set(true);
      requestPaymentMethods(
        account.authorityId || account.id,
        (response) => {
           filterAndSetPaymentMethods(response);
          Busy.set(false);
        },
        (error) => {
          const appError = getErrorMessageForNonStandardAndStandardResponse(error);
          console.error(appError);
          Busy.set(false);
        },
        true
      )
    }
  }, [account, filterAndSetPaymentMethods, isBrokeredBooking]);

  useEffect(() => loadPaymentMethods(), [loadPaymentMethods]);

  useEffect(() => {
    if (!account || !bookingLocation || !hasFinishedEditingBooking) return;

    const calculatedRate = calculateRate(assetType, bookingLocation, bookingStaticData.monthly);
    const {
      monthly,
      ignoreDate,
      chargeOverages,
      initialChargeAlreadyPaidValue,
      recurringChargeAlreadyPaidValue,
      partnerPayoutAmountPerPeriod,
      customerChargedAmountPerPeriod,
      overagePartnerPayoutRate,
      overageCustomerChargeRate,
    } = bookingStaticData;

    const endOfMonth = moment(getEndDateMonthly(startDate, 1)).format(DateFormats.DAY);
    const calculateCostObject = {
      supplierAccountId: bookingLocation.id,
      buyerAccountId: account.id,
      locationId: bookingLocation.locationId,
      numberOfSpaces: numberSpaces,
      startDate: moment(startDate).format(DateFormats.DAY),
      endDate: monthly ? endOfMonth : moment(endDate).format(DateFormats.DAY),
      frequency: monthly ? RECURRING : ONE_TIME,
      recurrences: monthly ? 9999 : 0,
      durationType: monthly ? MONTHLY : DAILY,
      rate: calculatedRate,
      assetType: assetType,
      paymentMethodId: selectedPaymentMethodId,
      brokeredBooking: isBrokeredBooking,
      ignoreDate: ignoreDate
    }

    if (isBrokeredBooking) {
      calculateCostObject.chargeOverages = chargeOverages;
      calculateCostObject.brokeredInitialChargeAlreadyPaid = initialChargeAlreadyPaidValue;
      calculateCostObject.brokeredRecurringChargeAlreadyPaid = recurringChargeAlreadyPaidValue;
      calculateCostObject.brokeredSupplierPaidPerOccurrence = partnerPayoutAmountPerPeriod;
      calculateCostObject.brokeredBuyerChargedPerOccurrence = customerChargedAmountPerPeriod;
      calculateCostObject.brokeredSupplierOverageRatePaid = overagePartnerPayoutRate;
      calculateCostObject.brokeredBuyerOverageRateCharged = overageCustomerChargeRate;
    }

    setLoadingCostData(true);

    let ignore = false;

    calculateBookingChargeAmount(calculateCostObject)
      .then(res => {
        if (!ignore) {
          const {body} = res;

          if (body) {
            const {monthly} = bookingStaticData;
            const {
              duration,
              initialBookingPaymentAmount,
              initialBookingPaymentProcessingFee,
              initialBookingChargeAmount,
              recurringBookingPaymentAmount,
              recurringBookingPaymentProcessingFee,
              recurringBookingChargeAmount,
              firstRecurringPaymentDate,
              initialBookingTaxAmount,
              recurringBookingTaxAmount
            } = body;

            setCostData({
              duration: monthly ? calculateNumberOfDays(calculateCostObject.startDate, calculateCostObject.endDate) : duration,
              initialTotalCharge: initialBookingChargeAmount,
              initialProcessingFee: initialBookingPaymentProcessingFee,
              initialCharge: initialBookingPaymentAmount,
              recurringTotalCharge: recurringBookingChargeAmount,
              recurringProcessingFee: recurringBookingPaymentProcessingFee,
              recurringCharge: recurringBookingPaymentAmount,
              firstRecurringPaymentDate: firstRecurringPaymentDate,
              initialBookingTaxAmount: initialBookingTaxAmount,
              recurringBookingTaxAmount: recurringBookingTaxAmount
            });
            setCostDataError(false);
          }
        }
      })
      .catch(err => {
        const appError = getErrorMessageForNonStandardAndStandardResponse(err);
        snackbarShowMessage(appError, 'error', 15000);
        setCostDataError(true);
      }).finally(() => setLoadingCostData(false));
    return () => ignore = true;
  }, [
    bookingLocation,
    account,
    numberSpaces,
    assetType,
    startDate,
    endDate,
    selectedPaymentMethodId,
    snackbarShowMessage,
    bookingStaticData,
    hasFinishedEditingBooking,
    isBrokeredBooking
  ]);

  const requestBookSpaceObject = useMemo(() => {
    const firstRecurringPaymentEndDate = moment(costData?.firstRecurringPaymentDate).subtract(1, 'day').format('MM/DD/YYYY');
    const requestBookSpaceObject = {
      supplierAccountId: bookingLocation?.id,
      buyerAccountId: account.id,
      locationId: bookingLocation?.locationId,
      numberOfSpaces: numberSpaces,
      startDate: startDate,
      endDate: bookingStaticData.monthly ? firstRecurringPaymentEndDate : moment(endDate).format(DateFormats.DAY),
      frequency: bookingStaticData.monthly ? RECURRING : ONE_TIME,
      recurrences: bookingStaticData.monthly ? 9999 : 0,
      durationType: bookingStaticData.monthly ? MONTHLY : DAILY,
      initialCharge: costData.initialTotalCharge,
      recurringCharge: costData.recurringTotalCharge,
      assetType: assetType,
      paymentMethodId: selectedPaymentMethodId,
      tosDocUrl: rentalAgreementURL,
      source: "MARKETPLACE",
      attributionSource: attributionSource || null,
      email: !isBrokeredBooking ? account.email : null,
      brokeredBooking: isBrokeredBooking,
      minNumberOfSpaces: bookingLocation?.minNumberOfSpaces ? bookingLocation?.minNumberOfSpaces : 1,
    };

    if (isBrokeredBooking) {
      requestBookSpaceObject.chargeOverages = bookingStaticData.chargeOverages;
      requestBookSpaceObject.brokeredInitialChargeAlreadyPaid = bookingStaticData.initialChargeAlreadyPaidValue;
      requestBookSpaceObject.brokeredRecurringChargeAlreadyPaid = bookingStaticData.recurringChargeAlreadyPaidValue;
      requestBookSpaceObject.brokeredSupplierPaidPerOccurrence = bookingStaticData.partnerPayoutAmountPerPeriod;
      requestBookSpaceObject.brokeredBuyerChargedPerOccurrence = bookingStaticData.customerChargedAmountPerPeriod;
      requestBookSpaceObject.brokeredSupplierOverageRatePaid = bookingStaticData.overagePartnerPayoutRate;
      requestBookSpaceObject.brokeredBuyerOverageRateCharged = bookingStaticData.overageCustomerChargeRate;
      requestBookSpaceObject.paymentMethodId = null;
      requestBookSpaceObject.manualPayment = bookingStaticData.manualPayment;
    }

    return requestBookSpaceObject;
  }, [bookingLocation, account, numberSpaces, bookingStaticData, startDate, endDate, costData, assetType, selectedPaymentMethodId, rentalAgreementURL, isBrokeredBooking, attributionSource]);

  const showPaymentModal = () => {
    setPaymentModalOpen(!paymentModalOpen);
  }

  const closePaymentModal = () => {
    setPaymentModalOpen(!paymentModalOpen);
    loadPaymentMethods();
  }

  const handleDisableBookingChanges = () => {
    setDisableBookingChanges((prevState) => !prevState);
  }

  const pageTitle = showConfirmation ? 'Confirmation' :
    locationHasRequestedCapacity ? 'Book Space' : 'Request Space';

  return (
    <>
      <PaymentMainSection component='section'>
        <PaymentContainer>
          <PaymentSection component='div'>
            <header>
              <PaymentHeading color={'textPrimary'} component={'h1'}>
                {pageTitle}
              </PaymentHeading>
            </header>
            <PaymentLpDivider variant='fullWidth'/>
            <PaymentMainContainer item container>
              <PaymentLpListingInfoSection item container direction={'column'} xs>
                {
                  showConfirmation ?
                    <BookingConfirmation
                      bookingNumber={bookingNumber}
                    />
                    : !isBrokeredBooking &&
                    <BookingPaymentInfo
                      account={account}
                      selectedPaymentMethod={selectedPaymentMethodId}
                      setSelectedPaymentMethod={setSelectedPaymentMethodId}
                      showPaymentModal={showPaymentModal}
                      paymentMethods={paymentMethods}
                    />
                }
              </PaymentLpListingInfoSection>
              <Grid item xs={12} md={5}>
                <BookingDetails
                  bookingLocation={bookingLocation}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  assetType={assetType}
                  setAssetType={setAssetType}
                  numberSpaces={numberSpaces}
                  setNumberSpaces={setNumberSpaces}
                  monthly={bookingStaticData.monthly}
                  costData={costData}
                  disableBookingChanges={disableBookingChanges}
                  instantApproval={locationHasRequestedCapacity}
                  isBrokeredBooking={isBrokeredBooking}
                  assetTypelist={assetTypeList}
                  calculatingCostData={loadingCostData}
                  onToggleEditMode={setHasFinishedEditingBooking}
                  displayCalculatedCostData={hasFinishedEditingBooking && !costDataError}
                />
              </Grid>
              <Grid item xs={12}>
                <RequestSpace
                  duration={costData.duration}
                  rentalAgreementURL={rentalAgreementURL}
                  hasAcceptedRentalAgreement={hasAcceptedRentalAgreement}
                  setHasAcceptedRentalAgreement={setHasAcceptedRentalAgreement}
                  requestBookSpaceObject={requestBookSpaceObject}
                  bookingLocation={bookingLocation}
                  setBookingNumber={setBookingNumber}
                  setShowConfirmation={setShowConfirmation}
                  disableBookingChanges={disableBookingChanges || !hasFinishedEditingBooking || costDataError}
                  handleDisableBookingChanges={handleDisableBookingChanges}
                  instantApproval={locationHasRequestedCapacity}
                  isBrokeredBooking={isBrokeredBooking}
                />
              </Grid>
            </PaymentMainContainer>
          </PaymentSection>
          <AddPaymentMethodSetup
            account={account}
            loadPaymentMethods={loadPaymentMethods}
            onClose={closePaymentModal}
            open={paymentModalOpen}
            onOpen={setPaymentModalOpen}
            ref={ref}
          />
        </PaymentContainer>
      </PaymentMainSection>
      <PaymentContainer>
        {bookingLocation && <LocationProfileSimilarYardsNearby location={bookingLocation}/>}
      </PaymentContainer>
    </>
  );
});

BookingPaymentBody.displayName = 'BookingPaymentBody';

export default BookingPaymentBody;
